<script setup>
import { computed, inject, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
]);

const router = useRouter();
const route = useRoute();
const toggleMainSidebar = inject('toggleMainSidebar', () => {});
const searchQuery = ref('');

const openSearch = () => {
  const query = searchQuery.value.trim();
  router.push({
    name: 'search',
    params: { accountId: route.params.accountId },
    query: query ? { q: query } : undefined,
  });
};

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});
</script>

<template>
  <div
    class="flex flex-col gap-2 px-3 py-3 bg-n-surface-1"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-center gap-2">
      <NextButton
        data-main-sidebar-launcher
        v-tooltip="'Главное меню'"
        icon="i-lucide-menu"
        slate
        sm
        faded
        class="md:hidden"
        @click="toggleMainSidebar"
      />
      <form
        class="flex flex-1 items-center h-10 px-3 gap-2 rounded-lg bg-n-slate-2 border border-n-weak focus-within:border-n-brand"
        @submit.prevent="openSearch"
      >
        <span class="i-lucide-search size-4 text-n-slate-10 shrink-0" />
        <input
          v-model="searchQuery"
          type="search"
          class="reset-base w-full min-w-0 bg-transparent border-0 shadow-none outline-none text-sm text-n-slate-12 placeholder:text-n-slate-10"
          placeholder="Поиск по имени, телефону или сообщению"
        />
      </form>
      <ComposeConversation align="start">
        <template #trigger="{ isOpen }">
          <NextButton
            v-tooltip="'Новый диалог'"
            icon="i-lucide-plus"
            blue
            sm
            class="!size-10"
            :class="{ '!bg-n-brand': isOpen }"
          />
        </template>
      </ComposeConversation>
    </div>
    <div class="flex items-center justify-end min-h-8 gap-1">
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <div v-else class="relative">
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          icon="i-lucide-list-filter"
          slate
          xs
          faded
          @click="emit('filtersModal')"
        />
        <div
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>
      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
    </div>
  </div>
</template>
